<template>
	<button
		type="button"
		class="l-filter-chip"
		:class="{ 'is-pressed': pressed }"
		:aria-pressed="pressed ? 'true' : 'false'"
		@click="onClick"
	>
		<span class="l-filter-chip-label">{{ label }}</span>
		<span v-if="hasCount" class="l-filter-chip-count">{{ count }}</span>
	</button>
</template>










<script setup>
/**
 * §8.5 FilterChip: a toggle button that narrows a list or grid ("Pending 4").
 *
 * It is a real button with `aria-pressed`, so the pressed state is announced, not only coloured.
 * It holds no state of its own: the page passes `pressed`, and the chip says what the user did
 * via `update:pressed` (the new value) and `select` (it was clicked). A page that treats a row of
 * chips as "pick one" handles `select` and sets which one is pressed; a page that treats them as
 * independent toggles uses `v-model:pressed`.
 */

import { computed } from 'vue';

//-- Props
const props = defineProps({
	label: {
		type: String,
		required: true,
	},

	/** A count shown after the label. Omit (null) for none; 0 is shown. */
	count: {
		type: Number,
		default: null,
	},

	pressed: {
		type: Boolean,
		default: false,
	},
});





//-- Emits
const emit = defineEmits(['update:pressed', 'select']);





//-- Computed Properties
const hasCount = computed(() => props.count !== null && props.count !== undefined);





//-- Methods
const onClick = () =>
{
	emit('update:pressed', !props.pressed);
	emit('select');
};
</script>










<style scoped>
.l-filter-chip
{
	display: inline-flex;
	align-items: center;
	gap: 7px;
	min-height: var(--target-min);
	padding: 4px 12px;
	border: 1px solid var(--line);
	border-radius: var(--r-pill);
	background: var(--surface);
	color: var(--text-2);
	font-family: var(--font-ui);
	font-size: var(--text-chip);
	font-weight: 600;
	cursor: pointer;
	white-space: nowrap;
	transition: background var(--t-fast) var(--ease), border-color var(--t-fast) var(--ease), color var(--t-fast) var(--ease);
}

.l-filter-chip:hover
{
	color: var(--text);
	border-color: var(--line-strong);
	background: var(--surface-sunken);
}

.l-filter-chip.is-pressed
{
	background: var(--interactive-tint);
	border-color: var(--interactive);
	color: var(--interactive-text);
}

.l-filter-chip-count
{
	font-variant-numeric: tabular-nums;
	font-weight: 700;
}
</style>
